import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, CalendarPlus, Phone, MapPin, Droplets,
  ShieldCheck, ShieldOff, Stethoscope, FileText, FlaskConical,
  X, Loader2, MoreVertical, Pencil, Download, Archive,
  ArchiveRestore, Clock, Eye, PlusCircle, RefreshCw,
  AlertTriangle, AlertCircle, CheckCircle2, LogOut, Shield, Activity,
  ExternalLink, Printer, CreditCard, ClipboardList,
} from 'lucide-react';
import { format, differenceInYears, parseISO } from 'date-fns';
import { clsx } from 'clsx';
import { patientApi, opdApi, appointmentsApi, api } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import OpdQuickViewDrawer from '@/features/patients/components/OpdQuickViewDrawer';

// ── Types ──────────────────────────────────────────────────────────
interface AuditLogEntry {
  event_id:      string;
  actor_name:    string | null;
  actor_role:    string;
  actor_ip:      string | null;
  action_type:   string;
  resource_type: string;
  resource_id:   string;
  purpose_code:  string | null;
  metadata:      Record<string, any> | null;
  created_at:    string;
}

// ── Helpers ────────────────────────────────────────────────────────
function ageLabel(p: any) {
  if (p?.dob) {
    try { return `${differenceInYears(new Date(), parseISO(p.dob))} yrs`; } catch {}
  }
  return p?.age_years ? `${p.age_years} yrs` : '—';
}
function genderFull(g?: string) {
  if (g === 'male')   return 'Male';
  if (g === 'female') return 'Female';
  if (g === 'other')  return 'Other';
  return '—';
}
function fmtDate(d?: string | null) {
  if (!d) return '—';
  try { return format(parseISO(d), 'dd MMM yyyy'); } catch { return d; }
}
function fmtDatetime(d?: string | null) {
  if (!d) return '—';
  try { return format(parseISO(d), 'dd MMM yyyy, HH:mm'); } catch { return d; }
}
function statusBadge(status: string) {
  const map: Record<string, string> = {
    waiting:     'bg-yellow-100 text-yellow-700',
    in_progress: 'bg-blue-100 text-blue-700',
    completed:   'bg-green-100 text-green-700',
    cancelled:   'bg-gray-100 text-gray-500',
    no_show:     'bg-red-100 text-red-600',
    scheduled:   'bg-indigo-50 text-indigo-600',
    confirmed:   'bg-teal-50 text-teal-600',
    arrived:     'bg-amber-100 text-amber-700',
  };
  return map[status] ?? 'bg-gray-100 text-gray-500';
}

// ── Audit action → icon + colour ──────────────────────────────────
function AuditIcon({ type }: { type: string }) {
  const map: Record<string, { icon: React.ReactNode; ring: string }> = {
    CREATE:             { icon: <PlusCircle  size={14}/>, ring: 'bg-green-100  text-green-600'  },
    UPDATE:             { icon: <Pencil      size={14}/>, ring: 'bg-blue-100   text-blue-600'   },
    VIEW:               { icon: <Eye         size={14}/>, ring: 'bg-gray-100   text-gray-500'   },
    DELETE:             { icon: <X           size={14}/>, ring: 'bg-red-100    text-red-600'    },
    CONSENT_GIVEN:      { icon: <CheckCircle2 size={14}/>, ring: 'bg-green-100 text-green-600'  },
    CONSENT_WITHDRAWN:  { icon: <AlertTriangle size={14}/>, ring: 'bg-orange-100 text-orange-500' },
    DATA_EXPORT:        { icon: <Download    size={14}/>, ring: 'bg-purple-100 text-purple-600' },
    LOGIN:              { icon: <Shield      size={14}/>, ring: 'bg-navy/10    text-navy'       },
    LOGOUT:             { icon: <LogOut      size={14}/>, ring: 'bg-gray-100   text-gray-500'   },
    PASSWORD_CHANGE:    { icon: <RefreshCw   size={14}/>, ring: 'bg-yellow-100 text-yellow-600' },
  };
  const { icon, ring } = map[type] ?? { icon: <Activity size={14}/>, ring: 'bg-gray-100 text-gray-500' };
  return (
    <span className={clsx('flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0', ring)}>
      {icon}
    </span>
  );
}

function auditLabel(entry: AuditLogEntry): string {
  const sub = entry.metadata?.subtype;
  if (sub === 'archived')   return 'Patient archived';
  if (sub === 'unarchived') return 'Patient unarchived';
  const map: Record<string, string> = {
    CREATE:            `${entry.resource_type.replace('_', ' ')} created`,
    UPDATE:            `${entry.resource_type.replace('_', ' ')} updated`,
    VIEW:              `${entry.resource_type.replace('_', ' ')} viewed`,
    DELETE:            `${entry.resource_type.replace('_', ' ')} deleted`,
    CONSENT_GIVEN:     'Consent recorded',
    CONSENT_WITHDRAWN: 'Consent withdrawn',
    DATA_EXPORT:       'Record exported',
    LOGIN:             'User logged in',
    LOGOUT:            'User logged out',
    PASSWORD_CHANGE:   'Password changed',
  };
  return map[entry.action_type] ?? entry.action_type;
}

function roleLabel(role: string) {
  const map: Record<string, string> = {
    hospital_admin: 'Admin', owner: 'Owner', doctor: 'Doctor',
    nurse: 'Nurse', receptionist: 'Receptionist', billing_staff: 'Billing',
    pharmacist: 'Pharmacist', lab_technician: 'Lab Tech', radiologist: 'Radiologist',
    accountant: 'Accountant',
  };
  return map[role] ?? role;
}

// ── New OPD Visit Modal ────────────────────────────────────────────
function NewVisitModal({
  patientId, onClose, onSuccess,
}: { patientId: string; onClose: () => void; onSuccess: () => void }) {
  const user       = useAuthStore(s => s.user);
  const isDoctor   = user?.role === 'doctor';
  const today      = format(new Date(), 'yyyy-MM-dd');

  const [complaint, setComplaint] = useState('');
  const [doctorId,  setDoctorId]  = useState(isDoctor ? user!.id : '');

  // Check if patient already has a scheduled appointment today — link it instead of creating walk-in
  const { data: apptData } = useQuery({
    queryKey: ['patient-today-appt', patientId, today],
    queryFn:  () => appointmentsApi.list({ date: today, patient_id: patientId }).then(r => r.data),
    staleTime: 30_000,
  });
  const linkedAppt = (apptData?.appointments ?? []).find(
    (a: any) => ['scheduled', 'confirmed'].includes(a.status),
  ) ?? null;

  // Pre-fill doctor from appointment when one is found
  useEffect(() => {
    if (linkedAppt?.doctor_id && !isDoctor && !doctorId) {
      setDoctorId(linkedAppt.doctor_id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedAppt?.doctor_id]);

  const { data: doctorsData } = useQuery({
    queryKey: ['opd-doctors'],
    queryFn:  () => opdApi.doctors().then(r => r.data),
    enabled:  !isDoctor,
  });
  const doctors = doctorsData?.doctors ?? [];

  const mutation = useMutation({
    mutationFn: () => {
      // If the selected doctor matches the booked appointment, use check-in to properly link them.
      // Otherwise create a fresh walk-in encounter (different doctor / override).
      const effectiveDoctor = doctorId || user!.id;
      if (linkedAppt && linkedAppt.doctor_id === effectiveDoctor) {
        return appointmentsApi.checkIn(linkedAppt.id, {
          chief_complaint: complaint.trim() || undefined,
        });
      }
      return opdApi.createEncounter({
        patient_id:      patientId,
        doctor_id:       effectiveDoctor,
        encounter_type:  'opd',
        chief_complaint: complaint.trim() || undefined,
      });
    },
    onSuccess: () => { onSuccess(); onClose(); },
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-navy">New OPD Visit</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18}/></button>
        </div>
        <div className="px-6 py-5 space-y-4">

          {/* Appointment banner — shown when a same-day booking is found */}
          {linkedAppt && (
            <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-200 rounded-xl px-3.5 py-3">
              <CalendarPlus size={14} className="text-blue-500 flex-shrink-0 mt-0.5"/>
              <div className="text-xs">
                <p className="font-semibold text-blue-800">Appointment found: {linkedAppt.appointment_no}</p>
                <p className="text-blue-600 mt-0.5">
                  Dr. {linkedAppt.doctor_name} · {linkedAppt.slot_start?.slice(0,5)}
                  {' '}— selecting this doctor will use the scheduled slot.
                </p>
              </div>
            </div>
          )}

          {!isDoctor && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                Assign Doctor <span className="text-red-400">*</span>
              </label>
              <select value={doctorId} onChange={e => setDoctorId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-navy
                           focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy/40">
                <option value="">Select a doctor</option>
                {doctors.map((d: any) => (
                  <option key={d.id} value={d.id}>
                    {d.name}{d.specialization ? ` — ${d.specialization}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Chief Complaint <span className="text-gray-400">(optional)</span>
            </label>
            <textarea value={complaint} onChange={e => setComplaint(e.target.value)} rows={3}
              placeholder="e.g. Fever since 2 days, headache…"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-navy resize-none
                         focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy/40
                         placeholder:text-gray-300"/>
          </div>
          {mutation.isError && (() => {
            const status = (mutation.error as any)?.response?.status;
            const msg    = (mutation.error as any)?.response?.data?.error;
            return status === 409 ? (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                <AlertCircle size={13} className="text-amber-600 flex-shrink-0 mt-0.5"/>
                <p className="text-xs text-amber-800">
                  {msg ?? 'Patient already has an active encounter in today\'s queue for this doctor.'}
                </p>
              </div>
            ) : (
              <p className="text-xs text-red-500">
                {msg ?? 'Failed to create encounter. Please try again.'}
              </p>
            );
          })()}
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
          <button onClick={() => mutation.mutate()}
            disabled={(!isDoctor && !doctorId) || mutation.isPending}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-orange text-gray-900
                       text-sm font-semibold hover:bg-orange-dark disabled:opacity-50 shadow-sm">
            {mutation.isPending ? <Loader2 size={14} className="animate-spin"/> : <CalendarPlus size={14}/>}
            Add to Today's Queue
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Demographics Modal ────────────────────────────────────────
function EditDemographicsModal({
  patient, onClose, onSuccess,
}: { patient: any; onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({
    name:                     patient.name ?? '',
    gender:                   patient.gender ?? 'unknown',
    dob:                      patient.dob ?? '',
    age_years:                patient.age_years ?? '',
    mobile:                   patient.mobile ?? '',
    email:                    patient.email ?? '',
    blood_group:              patient.blood_group ?? '',
    city:                     patient.city ?? '',
    state:                    patient.state ?? '',
    pin_code:                 patient.pin_code ?? '',
    address_line:             patient.address_line ?? '',
    emergency_contact_name:   patient.emergency_contact_name ?? '',
    emergency_contact_mobile: patient.emergency_contact_mobile ?? '',
    known_allergies:          (patient.known_allergies ?? []).join(', '),
    chronic_conditions:       (patient.chronic_conditions ?? []).join(', '),
    is_mlc:                   patient.is_mlc ?? false,
  });

  const mutation = useMutation({
    mutationFn: () => {
      // Build payload explicitly — never send empty strings for enum/regex fields
      // because Zod (and MySQL ENUMs) reject '' even with .optional()
      const payload: Record<string, any> = {
        name:         form.name,
        gender:       form.gender,
        dob:          form.dob          || undefined,
        mobile:       form.mobile       || undefined,
        age_years:    form.age_years    ? Number(form.age_years) : undefined,
        email:        form.email        || null,
        blood_group:  form.blood_group  || undefined,
        address_line: form.address_line || null,
        city:         form.city         || null,
        state:        form.state        || null,
        pin_code:     form.pin_code     || null,
        emergency_contact_name:   form.emergency_contact_name   || null,
        emergency_contact_mobile: form.emergency_contact_mobile || null,
        // known_allergies:    form.known_allergies.split(',').map(s => s.trim()).filter(Boolean),
        // chronic_conditions: form.chronic_conditions.split(',').map(s => s.trim()).filter(Boolean),
        known_allergies: form.known_allergies
  .split(',')
  .map((s: string) => s.trim())
  .filter(Boolean),

chronic_conditions: form.chronic_conditions
  .split(',')
  .map((s: string) => s.trim())
  .filter(Boolean),
        is_mlc: form.is_mlc,
      };
      // Strip undefined so keys are absent from the JSON body (Zod .optional() = not present)
      (Object.keys(payload) as string[]).forEach(k => {
        if (payload[k] === undefined) delete payload[k];
      });
      return patientApi.updateDemographics(patient.id, payload);
    },
    onSuccess: () => { onSuccess(); onClose(); },
  });

  const f = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [key]: e.target.value }));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="font-semibold text-navy flex items-center gap-2">
            <Pencil size={15}/> Edit Demographics
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18}/></button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-4 flex-1">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Full Name</label>
              <input value={form.name} onChange={f('name')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20"/>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Gender</label>
              <select value={form.gender} onChange={f('gender')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20">
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date of Birth</label>
              <input type="date" value={form.dob} onChange={f('dob')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20"/>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Mobile</label>
              <input value={form.mobile} onChange={f('mobile')} placeholder="10-digit mobile"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20"/>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
              <input type="email" value={form.email} onChange={f('email')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20"/>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Blood Group</label>
              <select value={form.blood_group} onChange={f('blood_group')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20">
                <option value="">— Select —</option>
                {['A+','A-','B+','B-','AB+','AB-','O+','O-','unknown'].map(g => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
              <input value={form.city} onChange={f('city')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20"/>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
              <input value={form.state} onChange={f('state')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20"/>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">PIN Code</label>
              <input value={form.pin_code} onChange={f('pin_code')} maxLength={6}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20"/>
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
              <input value={form.address_line} onChange={f('address_line')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20"/>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Emergency Contact Name</label>
              <input value={form.emergency_contact_name} onChange={f('emergency_contact_name')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20"/>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Emergency Contact Mobile</label>
              <input value={form.emergency_contact_mobile} onChange={f('emergency_contact_mobile')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20"/>
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Known Allergies <span className="text-gray-400">(comma-separated)</span>
              </label>
              <input value={form.known_allergies} onChange={f('known_allergies')}
                placeholder="e.g. Penicillin, Sulfa"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20"/>
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Chronic Conditions <span className="text-gray-400">(comma-separated)</span>
              </label>
              <input value={form.chronic_conditions} onChange={f('chronic_conditions')}
                placeholder="e.g. Diabetes, Hypertension"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20"/>
            </div>

            <div className="col-span-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={form.is_mlc}
                  onChange={e => setForm(p => ({ ...p, is_mlc: e.target.checked }))}
                  className="w-4 h-4 accent-orange"/>
                <span className="text-sm font-medium text-gray-700">Medico-Legal Case (MLC)</span>
                <span className="text-xs text-gray-400">— record cannot be archived while this flag is set</span>
              </label>
            </div>
          </div>

          {mutation.isError && (
            <p className="text-xs text-red-500 mt-2">
              {(mutation.error as any)?.response?.data?.error ?? 'Update failed. Please try again.'}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
          <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-navy text-white
                       text-sm font-semibold hover:bg-navy/90 disabled:opacity-50 shadow-sm">
            {mutation.isPending ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Archive Confirmation Modal ─────────────────────────────────────
function ArchiveModal({
  patient, onClose, onSuccess,
}: { patient: any; onClose: () => void; onSuccess: () => void }) {
  const isArchived  = patient.is_archived;
  const [mlcBlock,  setMlcBlock]  = useState(false);
  const [reason,    setReason]    = useState('');

  const mutation = useMutation({
    mutationFn: () => patientApi.archive(patient.id, {
      archive: !isArchived,
      reason:  reason.trim() || undefined,
    }),
    onSuccess: () => { onSuccess(); onClose(); },
  });

  const isBlocked = !isArchived && mlcBlock;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-navy flex items-center gap-2">
            {isArchived
              ? <><ArchiveRestore size={16} className="text-green-600"/> Unarchive Patient</>
              : <><Archive size={16} className="text-orange"/>            Archive Patient</>
            }
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18}/></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Warning banner */}
          {!isArchived && (
            <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5"/>
              <p className="text-sm text-amber-800">
                Archiving hides this patient from general searches but retains their data for legal compliance.
                Archived records can be restored at any time by a hospital administrator.
              </p>
            </div>
          )}

          {/* MLC checkbox */}
          {!isArchived && (
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={mlcBlock}
                  onChange={e => setMlcBlock(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-orange"/>
                <span className="text-sm text-gray-700">
                  Is this a <strong>Medico-Legal Case (MLC)</strong>?
                </span>
              </label>

              {mlcBlock && (
                <div className="flex gap-2 bg-red-50 border border-red-200 rounded-xl p-3 ml-7">
                  <AlertTriangle size={14} className="text-red-500 flex-shrink-0 mt-0.5"/>
                  <p className="text-xs text-red-700 font-medium">
                    MLC records must be retained indefinitely under NMC guidelines and cannot be archived.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Reason {isArchived ? '(optional)' : '(required for audit trail)'}
            </label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
              placeholder={isArchived ? 'Reason for unarchiving…' : 'Reason for archiving this patient record…'}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none
                         focus:outline-none focus:ring-2 focus:ring-navy/20 placeholder:text-gray-300"/>
          </div>

          {mutation.isError && (
            <p className="text-xs text-red-500">
              {(mutation.error as any)?.response?.data?.error ?? 'Action failed. Please try again.'}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={isBlocked || mutation.isPending || (!isArchived && !reason.trim())}
            className={clsx(
              'flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold shadow-sm disabled:opacity-40',
              isArchived
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-red-600 text-white hover:bg-red-700',
            )}>
            {mutation.isPending
              ? <Loader2 size={14} className="animate-spin"/>
              : isArchived ? <ArchiveRestore size={14}/> : <Archive size={14}/>
            }
            {isArchived ? 'Unarchive Patient' : 'Archive Patient'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Invoice status badge (navigates to /billing?invoice=INV-xxx) ──
const INVOICE_STATUS_CFG: Record<string, { cls: string; hover: string; label: string }> = {
  draft:          { cls: 'bg-amber-50 border-amber-200 text-amber-700',  hover: 'hover:bg-amber-100 hover:border-amber-300', label: 'Draft Invoice' },
  final:          { cls: 'bg-blue-50 border-blue-200 text-blue-700',     hover: 'hover:bg-blue-100 hover:border-blue-300',   label: 'Invoice Issued' },
  paid:           { cls: 'bg-green-50 border-green-200 text-green-700',  hover: 'hover:bg-green-100 hover:border-green-300', label: 'Paid' },
  partially_paid: { cls: 'bg-teal-50 border-teal-200 text-teal-700',     hover: 'hover:bg-teal-100 hover:border-teal-300',   label: 'Partial' },
  cancelled:      { cls: 'bg-gray-50 border-gray-200 text-gray-400',     hover: 'hover:bg-gray-100',                         label: 'Cancelled' },
};
const fmtRupee = (n: number) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

function InvoiceBadge({ status, invoiceNumber, amount }: {
  status: string | null; invoiceNumber: string | null; amount: number | null;
}) {
  if (!invoiceNumber) return null;
  const cfg = INVOICE_STATUS_CFG[status ?? 'draft'] ?? INVOICE_STATUS_CFG.draft;

  return (
    <Link
      to={`/billing?invoice=${invoiceNumber}`}
      onClick={e => e.stopPropagation()}
      className={clsx(
        'inline-flex items-center gap-1 text-2xs font-medium px-2 py-0.5 rounded-full border',
        'transition-all duration-150 cursor-pointer select-none',
        cfg.cls, cfg.hover,
      )}
    >
      <ClipboardList size={10}/>
      <span>{invoiceNumber}</span>
      {amount != null && amount > 0 && <span>· {fmtRupee(amount)}</span>}
      <span className="opacity-70">— {cfg.label}</span>
      <ExternalLink size={9} className="opacity-50"/>
    </Link>
  );
}

// ── Encounters Timeline Tab ────────────────────────────────────────
function EncountersTimeline({ patientId, patient }: { patientId: string; patient?: any }) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const [selectedEncounter, setSelectedEncounter] = useState<any>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['patient-encounters', patientId],
    queryFn:  () => patientApi.encounters(patientId).then(r => r.data),
  });
  const rows = data?.data ?? [];

  if (isLoading) return <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>;
  if (!rows.length) return (
    <div className="flex flex-col items-center py-20 text-gray-400 gap-3">
      <Stethoscope size={32} strokeWidth={1.2}/>
      <p className="text-sm">No encounters or upcoming appointments on record.</p>
    </div>
  );

  return (
    <>
    <div className="divide-y divide-gray-100">
      {rows.map((item: any) => {
        const isAppt    = item.record_type === 'appointment';
        const eventDate = item.event_date ?? '';
        const isFuture  = eventDate > today;
        const isActive  = !isAppt && eventDate === today &&
                          (item.status === 'waiting' || item.status === 'in_progress');

        return (
          <div
            key={item.id}
            className={clsx(
              'px-6 py-4 transition-colors',
              isActive  ? 'bg-amber-50 border-l-4 border-amber-400' :
              isFuture || isAppt
                        ? 'bg-gray-50/80 border-l-4 border-dashed border-gray-200' :
                          'hover:bg-gray-50',
            )}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-navy text-sm">{fmtDate(eventDate)}</span>
                  <span className={clsx('text-2xs px-2 py-0.5 rounded-full font-medium capitalize', statusBadge(item.status))}>
                    {item.status?.replace(/_/g, ' ')}
                  </span>
                  {/* Visit type badge */}
                  {(() => {
                    const raw = item.appointment_type || item.encounter_type;
                    const label = raw === 'follow_up'   ? 'Follow-up'
                                : raw === 'new'         ? 'New Patient'
                                : raw === 'procedure'   ? 'Procedure'
                                : raw === 'opd'         ? 'OPD'
                                : raw === 'emergency'   ? 'Emergency'
                                : raw === 'teleconsult' ? 'Teleconsult'
                                : raw ? String(raw).replace(/_/g, ' ') : null;
                    return label ? (
                      <span className="text-2xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                        {label}
                      </span>
                    ) : null;
                  })()}

                  {/* OPD encounter number — clickable, opens Quick View drawer */}
                  {!isAppt && item.encounter_no && (
                    <button
                      type="button"
                      onClick={() => setSelectedEncounter(item)}
                      className="font-mono text-2xs text-navy bg-navy/5 px-1.5 py-0.5 rounded
                                 hover:underline hover:bg-navy/10 transition font-medium"
                    >
                      {item.encounter_no}
                    </button>
                  )}
                  {/* Appointment reference sub-label — muted, non-navigating */}
                  {!isAppt && item.appointment_no && (
                    <span className="text-2xs text-gray-400 font-mono">
                      Ref: {item.appointment_no}
                    </span>
                  )}
                  {/* Upcoming appointment pill — links to appointments page */}
                  {isAppt && item.appointment_no && (
                    <Link
                      to={`/appointments?date=${eventDate}`}
                      onClick={e => e.stopPropagation()}
                      className="font-mono text-2xs text-blue-500 bg-blue-50 border border-blue-100
                                 px-1.5 py-0.5 rounded hover:bg-blue-100 transition"
                    >
                      {item.appointment_no}
                    </Link>
                  )}

                  {item.slot_start && (
                    <span className="text-2xs text-gray-400">
                      {item.slot_start.slice(0, 5)}{item.slot_end ? `–${item.slot_end.slice(0, 5)}` : ''}
                    </span>
                  )}

                  {/* Future appointment label */}
                  {isAppt && (
                    <span className="text-2xs text-indigo-500 font-medium">Upcoming</span>
                  )}
                  {/* Active today label */}
                  {isActive && (
                    <span className="text-2xs text-amber-600 font-semibold">Active Now</span>
                  )}
                </div>

                {/* Invoice badge (encounters only) */}
                {item.invoice_number && (
                  <div className="mt-1.5">
                    <InvoiceBadge
                      status={item.invoice_status}
                      invoiceNumber={item.invoice_number}
                      amount={item.invoice_amount}
                    />
                  </div>
                )}

                {item.chief_complaint && (
                  <p className="text-sm text-gray-600 mt-1 truncate" title={item.chief_complaint}>
                    {item.chief_complaint}
                  </p>
                )}
                {item.primary_icd10_desc && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    Dx: {item.primary_icd10_code} — {item.primary_icd10_desc}
                  </p>
                )}
                {item.soap_assessment && (
                  <p className="text-xs text-gray-500 mt-1 italic line-clamp-2">{item.soap_assessment}</p>
                )}
              </div>

              <div className="text-right flex-shrink-0">
                <div className="text-xs font-medium text-navy">{item.doctor_name}</div>
                {item.doctor_specialization && (
                  <div className="text-2xs text-gray-400">{item.doctor_specialization}</div>
                )}
                {item.follow_up_date && (
                  <div className="text-2xs text-orange mt-1">Follow-up: {fmtDate(item.follow_up_date)}</div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
    <OpdQuickViewDrawer
      encounter={selectedEncounter}
      patient={patient}
      onClose={() => setSelectedEncounter(null)}
    />
    </>
  );
}

// ── Audit & Activity Log Tab ───────────────────────────────────────
function AuditLogTab({ patientId }: { patientId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['patient-audit-log', patientId],
    queryFn:  () => patientApi.auditLog(patientId, { limit: 100 }).then(r => r.data),
  });
  const entries: AuditLogEntry[] = data?.data ?? [];

  if (isLoading) return <div className="py-16 text-center text-gray-400 text-sm">Loading audit trail…</div>;
  if (isError)   return (
    <div className="py-16 text-center text-gray-400 text-sm">
      <AlertTriangle size={24} className="mx-auto mb-2 opacity-40"/>
      Could not load audit log.
    </div>
  );
  if (!entries.length) return (
    <div className="flex flex-col items-center py-20 text-gray-400 gap-3">
      <Activity size={32} strokeWidth={1.2}/>
      <p className="text-sm">No activity recorded yet.</p>
    </div>
  );

  return (
    <div className="px-6 py-4">
      <div className="relative">
        {/* Vertical timeline line */}
        <div className="absolute left-3.5 top-0 bottom-0 w-px bg-gray-100" aria-hidden/>

        <ol className="space-y-5">
          {entries.map((entry, i) => (
            <li key={entry.event_id ?? i} className="relative flex gap-4 pl-1">
              <AuditIcon type={entry.action_type}/>
              <div className="flex-1 min-w-0 pt-0.5">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-medium text-navy">{auditLabel(entry)}</span>
                  {entry.metadata?.fields_updated && (
                    <span className="text-2xs text-gray-400">
                      ({(entry.metadata.fields_updated as string[]).join(', ')})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400 flex-wrap">
                  {entry.actor_name && (
                    <span className="font-medium text-gray-600">{entry.actor_name}</span>
                  )}
                  <span className="bg-gray-100 px-1.5 py-0.5 rounded text-2xs font-medium">
                    {roleLabel(entry.actor_role)}
                  </span>
                  {entry.actor_ip && <span>{entry.actor_ip}</span>}
                  <span className="flex items-center gap-1">
                    <Clock size={10}/>{fmtDatetime(entry.created_at)}
                  </span>
                </div>
                {entry.metadata?.reason && (
                  <p className="text-xs text-gray-500 mt-1 italic">"{entry.metadata.reason}"</p>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

// ── Source label derived from invoice context ─────────────────────
function sourceLabel(inv: any): { label: string; cls: string } {
  if (inv.encounter_id) return { label: 'OPD',      cls: 'bg-blue-50 text-blue-600' };
  if (inv.admission_id) return { label: 'IPD',      cls: 'bg-purple-50 text-purple-600' };
  return                       { label: 'Direct',   cls: 'bg-gray-100 text-gray-500' };
}

const INV_STATUS: Record<string, string> = {
  draft:          'bg-amber-50 text-amber-700 border border-amber-200',
  final:          'bg-blue-50 text-blue-700 border border-blue-200',
  paid:           'bg-green-50 text-green-700 border border-green-200',
  partially_paid: 'bg-teal-50 text-teal-700 border border-teal-200',
  cancelled:      'bg-gray-100 text-gray-400 border border-gray-200',
};
const INV_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', final: 'Issued', paid: 'Paid',
  partially_paid: 'Partial', cancelled: 'Cancelled',
};

// ── Invoices Tab — Financial Ledger ───────────────────────────────
function InvoicesTab({ patientId }: { patientId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['patient-invoices', patientId],
    queryFn:  () => api.get('/billing/invoices', { params: { patient_id: patientId, limit: 100 } }).then(r => r.data),
    staleTime: 30_000,
  });

  const invoices: any[] = data?.invoices ?? [];

  if (isLoading) return <div className="py-16 text-center text-gray-400 text-sm">Loading invoices…</div>;

  if (!invoices.length) return (
    <div className="flex flex-col items-center py-20 text-gray-400 gap-3">
      <ClipboardList size={32} strokeWidth={1.2}/>
      <p className="text-sm font-medium">No invoices on record.</p>
    </div>
  );

  const totals = invoices.reduce(
    (acc, inv) => ({
      billed:    acc.billed    + Number(inv.total_amount ?? 0),
      collected: acc.collected + Number(inv.amount_paid  ?? 0),
      due:       acc.due       + Number(inv.amount_due   ?? 0),
    }),
    { billed: 0, collected: 0, due: 0 },
  );

  return (
    <div>
      {/* Summary strip */}
      <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100 bg-gray-50/50">
        {[
          { label: 'Total Billed',    value: totals.billed,    cls: 'text-navy' },
          { label: 'Collected',       value: totals.collected, cls: 'text-green-600' },
          { label: 'Outstanding',     value: totals.due,       cls: totals.due > 0 ? 'text-red-500' : 'text-gray-400' },
        ].map(s => (
          <div key={s.label} className="px-6 py-3 text-center">
            <div className={clsx('text-base font-bold', s.cls)}>{fmtRupee(s.value)}</div>
            <div className="text-2xs text-gray-400 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Ledger table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/60 text-xs text-gray-500 uppercase tracking-wide">
              <th className="text-left px-6 py-3 font-medium">Date</th>
              <th className="text-left px-6 py-3 font-medium">Invoice No.</th>
              <th className="text-left px-6 py-3 font-medium">Source</th>
              <th className="text-right px-6 py-3 font-medium">Amount</th>
              <th className="text-right px-6 py-3 font-medium">Paid</th>
              <th className="text-left px-6 py-3 font-medium">Status</th>
              <th className="text-right px-6 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {invoices.map((inv: any) => {
              const src   = sourceLabel(inv);
              const isDue = ['draft','final','partially_paid'].includes(inv.status) && Number(inv.amount_due) > 0;
              return (
                <tr key={inv.id} className="hover:bg-gray-50/60 transition-colors group">
                  <td className="px-6 py-3.5 text-gray-600 whitespace-nowrap">
                    {inv.invoice_date ? format(new Date(inv.invoice_date), 'd MMM yyyy') : '—'}
                  </td>
                  <td className="px-6 py-3.5">
                    <Link
                      to={`/billing?invoice=${inv.invoice_number}`}
                      className="font-mono text-xs text-navy/80 hover:text-navy hover:underline flex items-center gap-1"
                    >
                      {inv.invoice_number}
                      <ExternalLink size={10} className="opacity-0 group-hover:opacity-50 transition-opacity"/>
                    </Link>
                  </td>
                  <td className="px-6 py-3.5">
                    <span className={clsx('text-2xs font-medium px-2 py-0.5 rounded-full', src.cls)}>
                      {src.label}
                    </span>
                  </td>
                  <td className="px-6 py-3.5 text-right font-medium text-navy tabular-nums">
                    {fmtRupee(Number(inv.total_amount))}
                  </td>
                  <td className="px-6 py-3.5 text-right text-gray-500 tabular-nums">
                    {fmtRupee(Number(inv.amount_paid))}
                  </td>
                  <td className="px-6 py-3.5">
                    <span className={clsx('text-2xs font-medium px-2 py-0.5 rounded-full', INV_STATUS[inv.status] ?? INV_STATUS.draft)}>
                      {INV_STATUS_LABEL[inv.status] ?? inv.status}
                    </span>
                  </td>
                  <td className="px-6 py-3.5">
                    <div className="flex items-center justify-end gap-1.5">
                      <Link
                        to={`/billing?invoice=${inv.invoice_number}`}
                        title="View / Print"
                        className="flex items-center gap-1 px-2 py-1 text-2xs text-gray-500
                                   border border-gray-200 rounded-lg hover:bg-gray-100 transition"
                      >
                        <Printer size={11}/> Print
                      </Link>
                      {isDue && (
                        <Link
                          to={`/billing?invoice=${inv.invoice_number}`}
                          title="Pay Now"
                          className="flex items-center gap-1 px-2 py-1 text-2xs text-white
                                     bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 transition"
                        >
                          <CreditCard size={11}/> Pay
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function LabReportsTab() {
  return (
    <div className="flex flex-col items-center py-20 text-gray-400 gap-3">
      <FlaskConical size={32} strokeWidth={1.2}/>
      <p className="text-sm font-medium">Lab Reports module coming soon</p>
    </div>
  );
}

// ── Actions Dropdown ───────────────────────────────────────────────
function ActionsDropdown({
  patient,
  onEdit,
  onExport,
  onArchive,
}: {
  patient:   any;
  onEdit:    () => void;
  onExport:  () => void;
  onArchive: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref             = useRef<HTMLDivElement>(null);
  const user            = useAuthStore(s => s.user);
  const isAdmin         = user && ['hospital_admin','owner'].includes(user.role);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200
                   text-sm text-gray-600 hover:bg-gray-50 transition">
        <MoreVertical size={15}/>
        Actions
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-52 bg-white rounded-xl border border-gray-200
                        shadow-lg z-30 py-1 overflow-hidden">
          <button
            onClick={() => { setOpen(false); onEdit(); }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
            <Pencil size={14} className="text-gray-400"/>
            Edit Demographics
          </button>
          <button
            onClick={() => { setOpen(false); onExport(); }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
            <Download size={14} className="text-gray-400"/>
            Export Full Record
          </button>
          {isAdmin && (
            <>
              <div className="border-t border-gray-100 my-1"/>
              <button
                onClick={() => { setOpen(false); onArchive(); }}
                className={clsx(
                  'w-full flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-gray-50',
                  patient.is_archived ? 'text-green-700' : 'text-red-600',
                )}>
                {patient.is_archived
                  ? <><ArchiveRestore size={14}/> Unarchive Patient</>
                  : <><Archive        size={14}/> Archive Patient</>
                }
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Export helper (browser print-to-PDF) ──────────────────────────
function exportPatientPdf(patient: any) {
  const allergies   = Array.isArray(patient.known_allergies)   ? patient.known_allergies.join(', ')   : '—';
  const conditions  = Array.isArray(patient.chronic_conditions) ? patient.chronic_conditions.join(', ') : '—';

  const html = `<!DOCTYPE html><html lang="en"><head>
    <meta charset="UTF-8"/>
    <title>Patient Record — ${patient.patient_uid}</title>
    <style>
      @page { size: A4; margin: 20mm; }
      body { font-family: Arial, sans-serif; color: #1a1a2e; font-size: 13px; }
      h1   { font-size: 18px; margin: 0 0 4px; }
      h2   { font-size: 13px; font-weight: 600; margin: 16px 0 6px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
      .uid { font-family: monospace; background:#f3f4f6; padding:2px 8px; border-radius:4px; font-size:12px; }
      table{ width:100%; border-collapse:collapse; }
      td   { padding:4px 8px 4px 0; vertical-align:top; }
      td:first-child { color:#6b7280; width:40%; font-weight:500; }
      .footer { margin-top:32px; font-size:11px; color:#9ca3af; border-top:1px solid #e5e7eb; padding-top:8px; }
    </style>
  </head><body>
    <h1>Patient Master Record</h1>
    <span class="uid">${patient.patient_uid}</span>
    <p style="color:#6b7280;font-size:11px;margin:4px 0 0">
      Exported ${new Date().toLocaleString('en-IN')} — Confidential
    </p>

    <h2>Demographics</h2>
    <table>
      <tr><td>Full Name</td><td>${patient.name ?? '—'}</td></tr>
      <tr><td>Gender</td><td>${patient.gender ?? '—'}</td></tr>
      <tr><td>Date of Birth</td><td>${patient.dob ?? '—'}</td></tr>
      <tr><td>Age</td><td>${patient.age_years ? `${patient.age_years} years` : '—'}</td></tr>
      <tr><td>Blood Group</td><td>${patient.blood_group ?? '—'}</td></tr>
      <tr><td>Mobile</td><td>${patient.mobile ?? '—'}</td></tr>
      <tr><td>Email</td><td>${patient.email ?? '—'}</td></tr>
      <tr><td>ABHA ID</td><td>${patient.abha_id ?? '—'}</td></tr>
    </table>

    <h2>Address</h2>
    <table>
      <tr><td>Address</td><td>${patient.address_line ?? '—'}</td></tr>
      <tr><td>City</td><td>${patient.city ?? '—'}</td></tr>
      <tr><td>State</td><td>${patient.state ?? '—'}</td></tr>
      <tr><td>PIN Code</td><td>${patient.pin_code ?? '—'}</td></tr>
    </table>

    <h2>Emergency Contact</h2>
    <table>
      <tr><td>Name</td><td>${patient.emergency_contact_name ?? '—'}</td></tr>
      <tr><td>Mobile</td><td>${patient.emergency_contact_mobile ?? '—'}</td></tr>
    </table>

    <h2>Clinical Flags</h2>
    <table>
      <tr><td>Known Allergies</td><td>${allergies}</td></tr>
      <tr><td>Chronic Conditions</td><td>${conditions}</td></tr>
      <tr><td>Medico-Legal Case</td><td>${patient.is_mlc ? 'YES — MLC' : 'No'}</td></tr>
      <tr><td>Archived</td><td>${patient.is_archived ? 'Yes' : 'No'}</td></tr>
      <tr><td>Registration Source</td><td>${patient.registration_source ?? '—'}</td></tr>
      <tr><td>Registered On</td><td>${patient.created_at ? new Date(patient.created_at).toLocaleString('en-IN') : '—'}</td></tr>
    </table>

    <div class="footer">
      This document is generated from Aarogya Saarthi HMIS and is subject to applicable data-protection and medical-records laws.
      Unauthorised reproduction or distribution is prohibited.
    </div>
  </body></html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); }, 400);
}

// ── Main Component ─────────────────────────────────────────────────
type Tab = 'visits' | 'invoices' | 'labs' | 'audit';

export default function PatientProfile() {
  const { id }         = useParams<{ id: string }>();
  const navigate       = useNavigate();
  const queryClient    = useQueryClient();
  const [tab, setTab]  = useState<Tab>('visits');
  const [showVisitModal,  setShowVisitModal]  = useState(false);
  const [showEditModal,   setShowEditModal]   = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  const { data: patient, isLoading, isError } = useQuery({
    queryKey: ['patient', id],
    queryFn:  () => patientApi.get(id!).then(r => r.data),
    enabled:  !!id,
  });

  const invalidatePatient = () => {
    queryClient.invalidateQueries({ queryKey: ['patient', id] });
    queryClient.invalidateQueries({ queryKey: ['patients'] });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 size={24} className="animate-spin"/>
      </div>
    );
  }
  if (isError || !patient) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
        <p className="text-sm">Patient not found.</p>
        <button onClick={() => navigate('/patients')} className="text-sm text-navy underline">
          Back to Directory
        </button>
      </div>
    );
  }

  const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'visits',   label: 'Encounters',          icon: <Stethoscope  size={13}/> },
    { key: 'invoices', label: 'Invoices',            icon: <FileText     size={13}/> },
    { key: 'labs',     label: 'Lab Reports',         icon: <FlaskConical size={13}/> },
    { key: 'audit',    label: 'Audit & Activity Log',icon: <Activity     size={13}/> },
  ];

  return (
    <div className="flex flex-col h-full bg-gray-50">

      {/* ── Back bar ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-6 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <button onClick={() => navigate('/patients')}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-navy transition">
          <ArrowLeft size={15}/> Patient Directory
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-sm font-medium text-navy truncate">{patient.name}</span>
      </div>

      {/* ── Archived banner ───────────────────────────────────────── */}
      {patient.is_archived && (
        <div className="flex items-center gap-2 px-6 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-800 text-sm flex-shrink-0">
          <Archive size={14} className="flex-shrink-0"/>
          <span>
            This patient record is <strong>archived</strong> and hidden from general searches.
            {patient.archived_at && ` Archived on ${fmtDate(patient.archived_at)}.`}
          </span>
        </div>
      )}

      {/* ── MLC banner ────────────────────────────────────────────── */}
      {patient.is_mlc && (
        <div className="flex items-center gap-2 px-6 py-2.5 bg-red-50 border-b border-red-200 text-red-800 text-sm flex-shrink-0">
          <AlertTriangle size={14} className="flex-shrink-0"/>
          <span><strong>Medico-Legal Case (MLC)</strong> — This record must be retained indefinitely per NMC guidelines.</span>
        </div>
      )}

      {/* ── Demographics header ───────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-6 py-5 flex-shrink-0">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-start gap-5">
            {/* Avatar */}
            <div className="w-14 h-14 rounded-2xl bg-navy/10 flex items-center justify-center
                            text-navy text-xl font-bold flex-shrink-0 select-none">
              {patient.name?.slice(0, 1).toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-lg font-bold text-navy">{patient.name}</h1>
                {patient.abha_id ? (
                  <span className="flex items-center gap-1 text-xs font-medium px-2 py-0.5
                                   rounded-full bg-green-100 text-green-700">
                    <ShieldCheck size={11}/> ABHA Linked
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs font-medium px-2 py-0.5
                                   rounded-full bg-gray-100 text-gray-400">
                    <ShieldOff size={11}/> No ABHA
                  </span>
                )}
                {patient.is_mlc && (
                  <span className="flex items-center gap-1 text-xs font-medium px-2 py-0.5
                                   rounded-full bg-red-100 text-red-600">
                    <AlertTriangle size={10}/> MLC
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap mt-1.5">
                <span className="font-mono text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  {patient.patient_uid}
                </span>
                <span className="text-sm text-gray-600">
                  {ageLabel(patient)} · {genderFull(patient.gender)}
                </span>
                {patient.blood_group && patient.blood_group !== 'unknown' && (
                  <span className="flex items-center gap-1 text-sm text-gray-600">
                    <Droplets size={13} className="text-red-400"/> {patient.blood_group}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 flex-wrap mt-2">
                {patient.mobile && (
                  <span className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Phone size={11}/> {patient.mobile}
                  </span>
                )}
                {(patient.city || patient.state) && (
                  <span className="flex items-center gap-1.5 text-xs text-gray-500">
                    <MapPin size={11}/> {[patient.city, patient.state].filter(Boolean).join(', ')}
                  </span>
                )}
                {(patient.known_allergies?.length ?? 0) > 0 && (
                  <span className="text-xs text-red-500 font-medium">
                    Allergies: {
                      typeof patient.known_allergies === 'string'
                        ? JSON.parse(patient.known_allergies).join(', ')
                        : patient.known_allergies.join(', ')
                    }
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right-side action buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => setShowVisitModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-orange text-gray-900
                         font-semibold text-sm hover:bg-orange-dark transition shadow-sm">
              <CalendarPlus size={15}/> + New OPD Visit
            </button>
            <ActionsDropdown
              patient={patient}
              onEdit={()    => setShowEditModal(true)}
              onExport={()  => exportPatientPdf(patient)}
              onArchive={() => setShowArchiveModal(true)}
            />
          </div>
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-6 flex-shrink-0">
        <div className="flex gap-1">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={clsx(
                'flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                tab === t.key
                  ? 'border-orange text-orange'
                  : 'border-transparent text-gray-500 hover:text-navy',
              )}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ──────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto bg-white">
        {tab === 'visits'   && <EncountersTimeline patientId={id!} patient={patient}/>}
        {tab === 'invoices' && <InvoicesTab patientId={id!}/>}
        {tab === 'labs'     && <LabReportsTab/>}
        {tab === 'audit'    && <AuditLogTab   patientId={id!}/>}
      </div>

      {/* ── Modals ───────────────────────────────────────────────── */}
      {showVisitModal && id && (
        <NewVisitModal
          patientId={id}
          onClose={()   => setShowVisitModal(false)}
          onSuccess={()  => queryClient.invalidateQueries({ queryKey: ['patient-encounters', id] })}
        />
      )}
      {showEditModal && (
        <EditDemographicsModal
          patient={patient}
          onClose={()   => setShowEditModal(false)}
          onSuccess={()  => {
            invalidatePatient();
            queryClient.invalidateQueries({ queryKey: ['patient-audit-log', id] });
          }}
        />
      )}
      {showArchiveModal && (
        <ArchiveModal
          patient={patient}
          onClose={()   => setShowArchiveModal(false)}
          onSuccess={()  => {
            invalidatePatient();
            queryClient.invalidateQueries({ queryKey: ['patient-audit-log', id] });
          }}
        />
      )}
    </div>
  );
}
